'use client';

// module imports
import { zodResolver } from '@hookform/resolvers/zod';
import { signIn } from 'next-auth/react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

// file imports
import { EmailVerificationPrompt, FormField } from '@/components';

const schema = z.object({
  email: z.string().min(1, 'Email is required').email({ message: 'Please enter a valid email' }),
  password: z.string().min(1, 'Password is required').min(6, { message: 'Password must be at least 6 characters' }),
});

type LoginFormData = z.infer<typeof schema>;

interface LoginFormProps {
  onSuccess: () => void;
}

export default function LoginForm({ onSuccess }: LoginFormProps) {
  const [errorMsg, setErrorMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [showVerificationPrompt, setShowVerificationPrompt] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    setLoading(true);
    setErrorMsg('');

    // Lowercase the email before sending
    const lowercasedEmail = data.email.toLowerCase();

    const res: any = await signIn('credentials', {
      email: lowercasedEmail,
      password: data.password,
      redirect: false,
      callbackUrl: '/dashboard',
    });

    setLoading(false);

    if (res?.error) {
      // Check if it's an email verification error
      if (res.error.includes('verify your email')) {
        setUserEmail(lowercasedEmail);
        setShowVerificationPrompt(true);
        setErrorMsg('');
      } else {
        setErrorMsg('Invalid email or password');
      }
      return;
    }

    onSuccess();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full">
      <FormField id="email" label="Email Address" type="email" placeholder="Enter your email" {...register('email')} error={errors.email?.message} required />
      <div className="mt-2"></div>
      <FormField id="password" label="Password" type="password" placeholder="Enter your password" {...register('password')} error={errors.password?.message} required />
      {errorMsg && <div className="text-red-600 mb-3 text-sm">{errorMsg}</div>}
      <div className="mt-3"></div>
      <button type="submit" className="w-full py-3 bg-[#5167b2] text-white font-bold rounded-lg hover:bg-[#4158a2] cursor-pointer transition" disabled={loading}>
        {loading ? 'Logging in...' : 'LOGIN'}
      </button>

      {showVerificationPrompt && userEmail && <EmailVerificationPrompt email={userEmail} onResendSuccess={() => setShowVerificationPrompt(false)} onResendError={(error) => setErrorMsg(error)} onClose={() => setShowVerificationPrompt(false)} />}
    </form>
  );
}
